import { ipcCall } from '@/composables/useIPC'
import { IpcCommand } from '@/types/ipc'
import type {
  AnalysisJobExecutor,
  AnalysisJobPolicy,
  AnalysisJobStatus,
  CreateAnalysisJobInput,
  LocalAnalysisJob,
  SchedulerStartOptions,
  SchedulerSkipReason,
  SchedulerStartResult,
} from './types'

export const DEFAULT_ANALYSIS_JOB_POLICY: AnalysisJobPolicy = {
  cooldownSeconds: 300,
  maxConcurrent: 2,
  dailyLimit: 0,
  retryDelaysSeconds: [30, 120, 300, 900],
  circuitBreakerFailureThreshold: 3,
  circuitBreakerCooldownSeconds: 180,
}

const RUNNABLE_STATUSES = new Set<AnalysisJobStatus>(['queued', 'retryable_failed'])
const TERMINAL_STATUSES = new Set<AnalysisJobStatus>(['synced', 'canceled', 'failed'])
const CIRCUIT_BREAKER_ERROR_CODES = new Set(['stream_timeout', 'stream_disconnected', 'relay_unavailable'])
const RETRY_JITTER_ERROR_CODES = new Set(['stream_timeout', 'stream_disconnected', 'relay_unavailable'])
const PERMANENT_CUSTOMER_ACCESS_ERROR_CODE = 'customer_access_denied'
const PERMANENT_DEVICE_ACCESS_ERROR_CODE = 'device_access_denied'
const PERMANENT_CUSTOMER_ACCESS_ERROR_PATTERNS = [
  /客户不存在或无权访问/,
  /customer(?:\s|_)*(not\s*found|access\s*denied|unauthorized)/i,
]
const PERMANENT_DEVICE_ACCESS_ERROR_PATTERNS = [
  /设备无权访问/,
  /device(?:\s|_)*(access\s*denied|unauthorized)/i,
]
const RETRY_JITTER_MAX_SECONDS = 60
const activeRunningSchedulers = new Set<AnalysisJobScheduler>()

export interface AnalysisJobOwnerIdentity {
  companyId: number
  userId: number
}

interface RunningAnalysisJob {
  controller: AbortController
  job: LocalAnalysisJob
}

export class AnalysisJobScheduler {
  private policy: AnalysisJobPolicy
  private executor?: AnalysisJobExecutor
  private readonly getCurrentIdentity?: () => AnalysisJobOwnerIdentity | null
  private readonly running = new Map<string, RunningAnalysisJob>()
  private readonly lastStartedAt = new Map<string, number>()
  private draining = false
  private consecutiveWeakNetworkFailures = 0
  private autoDrainPausedUntilMs = 0

  constructor(options: {
    policy?: Partial<AnalysisJobPolicy>
    executor?: AnalysisJobExecutor
    getCurrentIdentity?: () => AnalysisJobOwnerIdentity | null
  } = {}) {
    this.policy = normalizePolicy(options.policy)
    this.executor = options.executor
    this.getCurrentIdentity = options.getCurrentIdentity
  }

  setPolicy(policy?: Partial<AnalysisJobPolicy>) {
    this.policy = normalizePolicy(policy)
  }

  setExecutor(executor: AnalysisJobExecutor) {
    this.executor = executor
  }

  async createJob(input: CreateAnalysisJobInput): Promise<LocalAnalysisJob> {
    validateCreateInput(input)
    const sourceWatermark = input.sourceWatermark || ''
    const localJobId = input.localJobId || buildLocalAnalysisJobId()
    const owner = extractPayloadOwner(input.payload)
    const idempotencyKey = await buildAnalysisJobIdempotencyKey({
      customerId: input.customerId,
      conversationId: input.conversationId,
      analysisType: input.analysisType,
      triggerType: input.triggerType,
      triggerAccountWxId: input.triggerAccountWxId || '',
      sourceWatermark,
      templateVersion: input.templateVersion,
      companyId: owner.companyId,
      userId: owner.userId,
      nonce: input.idempotencyNonce || '',
    })

    const job = await ipcCall<LocalAnalysisJob>(IpcCommand.AnalysisJobCreate, {
      input: {
        localJobId,
        bossJobId: input.bossJobId || '',
        companyId: owner.companyId,
        userId: owner.userId,
        customerId: input.customerId,
        conversationId: input.conversationId || '',
        analysisType: input.analysisType,
        triggerType: input.triggerType,
        policyVersion: input.policyVersion || '',
        templateVersion: input.templateVersion,
        contextHash: input.contextHash || '',
        sourceWatermark,
        idempotencyKey,
        payloadJson: JSON.stringify(input.payload || {}),
        relayTokenExpiresAt: input.relayTokenExpiresAt || 0,
      },
    })
    return { ...job, wasCreated: job.localJobId === localJobId }
  }

  async start(optionsOrNowMs: SchedulerStartOptions | number = Date.now()): Promise<SchedulerStartResult> {
    const options = typeof optionsOrNowMs === 'number'
      ? { nowMs: optionsOrNowMs }
      : optionsOrNowMs
    const nowMs = options.nowMs ?? Date.now()
    const pending = await this.listPending()
    const started: LocalAnalysisJob[] = []
    const skipped: SchedulerSkipReason[] = []
    const autoCircuitOpen =
      (!options.onlyLocalJobId || options.respectCircuitBreaker === true)
      && this.autoDrainPausedUntilMs > nowMs
    const startedToday = this.policy.dailyLimit > 0
      ? await this.countStartedSince(beijingDayStartUnix(nowMs))
      : 0
    let plannedCount = this.running.size

    const runnablePending = options.onlyLocalJobId
      ? pending.filter((job) => job.localJobId === options.onlyLocalJobId)
      : pending

    for (const job of runnablePending) {
      if (!RUNNABLE_STATUSES.has(job.status)) continue
      const permanentFailure = resolvePermanentAccessFailure(job)
      if (job.status === 'retryable_failed' && permanentFailure) {
        await this.markFailed(
          job.localJobId,
          permanentFailure.errorCode,
          job.errorMessage || permanentFailure.message,
        )
        skipped.push(skip(job, 'permanent_failure', permanentFailure.message))
        continue
      }
      if (autoCircuitOpen) {
        skipped.push(skip(job, 'circuit_open', '弱网连续失败，自动分析暂缓重试'))
        continue
      }
      if (
        job.status === 'retryable_failed'
        && job.nextRetryAt > Math.floor(nowMs / 1000)
        && !options.ignoreRetryDelay
      ) {
        skipped.push(skip(job, 'retry_not_due', '重试时间未到'))
        continue
      }
      const cooldownKey = buildJobCooldownKey(job)
      const lastAt = this.lastStartedAt.get(cooldownKey) || 0
      if (nowMs - lastAt < this.policy.cooldownSeconds * 1000) {
        skipped.push(skip(job, 'cooldown', '客户自动分析冷却期内'))
        continue
      }
      if (!this.executor) {
        skipped.push(skip(job, 'executor_missing', '后台分析执行器未注册'))
        continue
      }
      if (plannedCount >= this.policy.maxConcurrent) {
        skipped.push(skip(job, 'max_concurrent', '后台分析并发已达上限'))
        continue
      }
      if (this.policy.dailyLimit > 0 && startedToday + started.length >= this.policy.dailyLimit) {
        skipped.push(skip(job, 'daily_limit', '后台分析每日上限已达成'))
        continue
      }

      const runningJob = await this.updateStatus(job.localJobId, 'running', {
        expectedStatus: job.status,
      })
      this.lastStartedAt.set(cooldownKey, nowMs)
      plannedCount += 1
      started.push(runningJob)
      void this.runExecutor(runningJob, options.drainOnSettled !== false)
    }

    return { started, skipped }
  }

  async drain(nowMs = Date.now()): Promise<SchedulerStartResult> {
    if (this.draining) return { started: [], skipped: [] }
    this.draining = true
    try {
      return await this.start({ nowMs })
    } finally {
      this.draining = false
    }
  }

  listPending(): Promise<LocalAnalysisJob[]> {
    return ipcCall<LocalAnalysisJob[]>(IpcCommand.AnalysisJobListPending)
      .then((jobs) => this.filterJobsForCurrentIdentity(jobs))
  }

  recoverRunningJobs(): Promise<number> {
    return ipcCall<number>(IpcCommand.AnalysisJobRecoverRunning)
  }

  countStartedSince(sinceAt: number): Promise<number> {
    return ipcCall<number>(IpcCommand.AnalysisJobCountStartedSince, {
      input: { sinceAt },
    })
  }

  async cancel(localJobId: string): Promise<LocalAnalysisJob> {
    const running = this.running.get(localJobId)
    if (running) {
      running.controller.abort()
      this.running.delete(localJobId)
      this.unregisterRunningSchedulerIfIdle()
    }
    return ipcCall<LocalAnalysisJob>(IpcCommand.AnalysisJobCancel, {
      input: { localJobId },
    })
  }

  async cancelRunningJobsForOtherIdentity(identity: AnalysisJobOwnerIdentity): Promise<LocalAnalysisJob[]> {
    const jobs = Array.from(this.running.values())
      .map((item) => item.job)
      .filter((job) => !isJobOwnedByIdentity(job, identity))
    const canceled: LocalAnalysisJob[] = []
    for (const job of jobs) {
      canceled.push(await this.cancel(job.localJobId))
    }
    return canceled
  }

  markLocalDone(localJobId: string, bossJobId?: string): Promise<LocalAnalysisJob> {
    this.consecutiveWeakNetworkFailures = 0
    this.autoDrainPausedUntilMs = 0
    return this.updateStatus(localJobId, 'local_done', { bossJobId })
  }

  markFailed(localJobId: string, errorCode = 'failed', errorMessage = ''): Promise<LocalAnalysisJob> {
    return this.updateStatus(localJobId, 'failed', { errorCode, errorMessage })
  }

  markRetryableFailed(
    localJobId: string,
    retryCount: number,
    errorCode = 'retryable_failed',
    errorMessage = '',
    nowMs = Date.now(),
    minRetryDelaySeconds = 0,
  ): Promise<LocalAnalysisJob> {
    this.recordRetryableFailure(errorCode, nowMs)
    const retryDelaySeconds = resolveRetryDelaySeconds(
      retryCount,
      this.policy,
      RETRY_JITTER_ERROR_CODES.has(errorCode) ? `${localJobId}:${retryCount}:${errorCode}` : '',
    )
    const boundedMinDelaySeconds = normalizeNonNegativeInt(minRetryDelaySeconds, 0)
    const nextRetryAt = Math.floor(nowMs / 1000) + Math.max(retryDelaySeconds, boundedMinDelaySeconds)
    return ipcCall<LocalAnalysisJob>(IpcCommand.AnalysisJobMarkRetry, {
      input: {
        localJobId,
        retryCount,
        nextRetryAt,
        errorCode,
        errorMessage,
      },
    })
  }

  isTerminal(status: AnalysisJobStatus) {
    return TERMINAL_STATUSES.has(status)
  }

  private updateStatus(
    localJobId: string,
    status: AnalysisJobStatus,
    options: {
      bossJobId?: string
      errorCode?: string
      errorMessage?: string
      expectedStatus?: AnalysisJobStatus
    } = {},
  ): Promise<LocalAnalysisJob> {
    return ipcCall<LocalAnalysisJob>(IpcCommand.AnalysisJobUpdateStatus, {
      input: {
        localJobId,
        status,
        expectedStatus: options.expectedStatus,
        bossJobId: options.bossJobId,
        errorCode: options.errorCode,
        errorMessage: options.errorMessage,
      },
    })
  }

  private recordRetryableFailure(errorCode: string, nowMs: number) {
    if (!CIRCUIT_BREAKER_ERROR_CODES.has(errorCode)) {
      this.consecutiveWeakNetworkFailures = 0
      return
    }
    this.consecutiveWeakNetworkFailures += 1
    if (this.consecutiveWeakNetworkFailures >= this.policy.circuitBreakerFailureThreshold) {
      this.autoDrainPausedUntilMs = nowMs + this.policy.circuitBreakerCooldownSeconds * 1000
    }
  }

  private filterJobsForCurrentIdentity(jobs: LocalAnalysisJob[]): LocalAnalysisJob[] {
    if (!this.getCurrentIdentity) return jobs
    const identity = this.getCurrentIdentity()
    if (!identity?.companyId || !identity?.userId) return []
    return jobs.filter((job) => isJobOwnedByIdentity(job, identity))
  }

  private async runExecutor(job: LocalAnalysisJob, drainOnSettled: boolean) {
    if (!this.executor) return
    const controller = new AbortController()
    this.running.set(job.localJobId, { controller, job })
    activeRunningSchedulers.add(this)
    try {
      await this.executor.run(job, controller.signal)
      if (!controller.signal.aborted && !this.executor.handlesFinalStatus) {
        await this.markLocalDone(job.localJobId)
      }
    } catch (e) {
      if (controller.signal.aborted) return
      await this.markRetryableFailed(
        job.localJobId,
        job.retryCount + 1,
        'executor_error',
        e instanceof Error ? e.message : String(e),
      )
    } finally {
      this.running.delete(job.localJobId)
      this.unregisterRunningSchedulerIfIdle()
      if (drainOnSettled) {
        setTimeout(() => {
          void this.drain()
        }, 0)
      }
    }
  }

  private unregisterRunningSchedulerIfIdle() {
    if (this.running.size === 0) {
      activeRunningSchedulers.delete(this)
    }
  }
}

export async function cancelRunningAnalysisJobsForOtherIdentity(
  identity: AnalysisJobOwnerIdentity,
): Promise<LocalAnalysisJob[]> {
  const canceled: LocalAnalysisJob[] = []
  for (const scheduler of Array.from(activeRunningSchedulers)) {
    canceled.push(...await scheduler.cancelRunningJobsForOtherIdentity(identity))
  }
  return canceled
}

export async function enqueueAutoAnalysisJob(input: {
  customerId: number
  conversationId: string
  customerNickname?: string
  customerTitle?: string
  policyVersion?: string
  templateVersion?: number
  policyHash?: string
  templateHash?: string
  skipTraceSource?: string
  contextHash?: string
  sourceWatermark?: string
}) {
  const scheduler = new AnalysisJobScheduler()
  return scheduler.createJob({
    customerId: input.customerId,
    conversationId: input.conversationId,
    analysisType: 'coach_feedback',
    triggerType: 'auto',
    policyVersion: input.policyVersion || '',
    templateVersion: input.templateVersion || 1,
    contextHash: input.contextHash || '',
    sourceWatermark: input.sourceWatermark || '',
    payload: {
      customerNickname: input.customerNickname || '',
      customerTitle: input.customerTitle || '',
      policyHash: input.policyHash || '',
      templateHash: input.templateHash || '',
      skipTraceSource: input.skipTraceSource || '',
    },
  })
}

export async function buildAnalysisJobIdempotencyKey(input: {
  customerId: number
  conversationId: string
  analysisType: string
  triggerType?: string
  triggerAccountWxId?: string
  companyId?: number
  userId?: number
  sourceWatermark: string
  templateVersion: number
  nonce?: string
}) {
  const companyId = positiveNumber(input.companyId)
  const userId = positiveNumber(input.userId)
  const ownerParts = companyId && userId ? [String(companyId), String(userId)] : []
  const parts = [
    ...ownerParts,
    String(input.customerId),
    input.conversationId || '',
    input.analysisType,
    input.triggerType || '',
    input.triggerAccountWxId || '',
    input.sourceWatermark || '',
    String(input.templateVersion),
  ]
  if (input.nonce) {
    parts.push(input.nonce)
  }
  const raw = parts.join('\n')
  const hex = await sha256Hex(raw)
  return `sha256:${hex}`
}

function buildLocalAnalysisJobId(nowMs = Date.now()) {
  return `local-${Math.floor(nowMs / 1000)}-${randomHex16()}`
}

export function resolveRetryDelaySeconds(
  retryCount: number,
  policy = DEFAULT_ANALYSIS_JOB_POLICY,
  jitterSeed = '',
) {
  const delays = policy.retryDelaysSeconds.length > 0
    ? policy.retryDelaysSeconds
    : DEFAULT_ANALYSIS_JOB_POLICY.retryDelaysSeconds
  const index = Math.max(0, Math.min(retryCount - 1, delays.length - 1))
  const baseDelay = delays[index]
  if (!jitterSeed) return baseDelay
  return baseDelay + deterministicJitterSeconds(jitterSeed, Math.min(baseDelay, RETRY_JITTER_MAX_SECONDS))
}

function validateCreateInput(input: CreateAnalysisJobInput) {
  if (!Number.isFinite(input.customerId) || input.customerId <= 0) {
    throw new Error('customerId 必须大于 0')
  }
  if (!input.analysisType) {
    throw new Error('analysisType 不能为空')
  }
  if (!Number.isFinite(input.templateVersion) || input.templateVersion <= 0) {
    throw new Error('templateVersion 必须大于 0')
  }
}

function normalizePolicy(policy?: Partial<AnalysisJobPolicy>): AnalysisJobPolicy {
  return {
    cooldownSeconds: normalizeNonNegativeInt(
      policy?.cooldownSeconds,
      DEFAULT_ANALYSIS_JOB_POLICY.cooldownSeconds,
    ),
    maxConcurrent: normalizePositiveInt(
      policy?.maxConcurrent,
      DEFAULT_ANALYSIS_JOB_POLICY.maxConcurrent,
    ),
    dailyLimit: normalizeNonNegativeInt(
      policy?.dailyLimit,
      DEFAULT_ANALYSIS_JOB_POLICY.dailyLimit,
    ),
    retryDelaysSeconds: policy?.retryDelaysSeconds?.length
      ? policy.retryDelaysSeconds.map((item) => normalizePositiveInt(item, 30))
      : [...DEFAULT_ANALYSIS_JOB_POLICY.retryDelaysSeconds],
    circuitBreakerFailureThreshold: normalizePositiveInt(
      policy?.circuitBreakerFailureThreshold,
      DEFAULT_ANALYSIS_JOB_POLICY.circuitBreakerFailureThreshold,
    ),
    circuitBreakerCooldownSeconds: normalizePositiveInt(
      policy?.circuitBreakerCooldownSeconds,
      DEFAULT_ANALYSIS_JOB_POLICY.circuitBreakerCooldownSeconds,
    ),
  }
}

function normalizePositiveInt(value: number | undefined, fallback: number) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 1) return fallback
  return Math.floor(n)
}

function normalizeNonNegativeInt(value: number | undefined, fallback: number) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return fallback
  return Math.floor(n)
}

function deterministicJitterSeconds(seed: string, maxSeconds: number) {
  const max = normalizeNonNegativeInt(maxSeconds, 0)
  if (!seed || max <= 0) return 0
  let hash = 2166136261
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0) % (max + 1)
}

function beijingDayStartUnix(nowMs: number) {
  const dayMs = 24 * 60 * 60 * 1000
  const beijingOffsetMs = 8 * 60 * 60 * 1000
  return Math.floor((Math.floor((nowMs + beijingOffsetMs) / dayMs) * dayMs - beijingOffsetMs) / 1000)
}

function skip(
  job: LocalAnalysisJob,
  reason: SchedulerSkipReason['reason'],
  message: string,
): SchedulerSkipReason {
  return { localJobId: job.localJobId, reason, message }
}

function buildJobCooldownKey(job: LocalAnalysisJob): string {
  const payload = parseJobPayload(job.payloadJson)
  const companyId = positiveNumber(job.companyId) || positiveNumber(payload.companyId)
  const userId = positiveNumber(job.userId) || positiveNumber(payload.userId)
  const bdWxId = stringValue(payload.bdWxId)
  const conversationId = job.conversationId || ''
  if (companyId && userId && bdWxId && conversationId) {
    return [
      companyId,
      userId,
      bdWxId,
      job.customerId,
      conversationId,
    ].join('::')
  }
  return [
    'legacy',
    job.customerId,
    conversationId,
  ].join('::')
}

function parseJobPayload(payloadJson: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(payloadJson || '{}')
    return parsed && typeof parsed === 'object'
      ? parsed as Record<string, unknown>
      : {}
  } catch {
    return {}
  }
}

function extractPayloadOwner(payload: Record<string, unknown> | undefined): AnalysisJobOwnerIdentity {
  return {
    companyId: positiveNumber(payload?.companyId),
    userId: positiveNumber(payload?.userId),
  }
}

function isJobOwnedByIdentity(
  job: LocalAnalysisJob,
  identity: AnalysisJobOwnerIdentity,
): boolean {
  const owner = extractPayloadOwner(parseJobPayload(job.payloadJson))
  const companyId = positiveNumber(job.companyId) || owner.companyId
  const userId = positiveNumber(job.userId) || owner.userId
  if (!companyId || !userId) return false
  return companyId === identity.companyId && userId === identity.userId
}

function positiveNumber(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function resolvePermanentAccessFailure(job: LocalAnalysisJob): { errorCode: string; message: string } | null {
  const errorMessage = job.errorMessage || ''
  if (!errorMessage) return null
  if (PERMANENT_CUSTOMER_ACCESS_ERROR_PATTERNS.some((pattern) => pattern.test(errorMessage))) {
    return {
      errorCode: PERMANENT_CUSTOMER_ACCESS_ERROR_CODE,
      message: '客户不可访问，已停止自动重试',
    }
  }
  if (PERMANENT_DEVICE_ACCESS_ERROR_PATTERNS.some((pattern) => pattern.test(errorMessage))) {
    return {
      errorCode: PERMANENT_DEVICE_ACCESS_ERROR_CODE,
      message: '设备不可访问，已停止自动重试',
    }
  }
  return null
}

async function sha256Hex(text: string): Promise<string> {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
      return Array.from(new Uint8Array(buffer))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
    }
  } catch {
    // 使用 deterministic fallback，保证旧 WebView/测试环境仍能稳定生成同一 key。
  }
  return fallbackHashHex(text)
}

function randomHex16() {
  try {
    const bytes = new Uint8Array(8)
    globalThis.crypto?.getRandomValues(bytes)
    if (bytes.some((byte) => byte !== 0)) {
      return Array.from(bytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
    }
  } catch {
    // Fall back below for test/legacy runtimes without crypto.getRandomValues.
  }
  return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
    .toString(16)
    .padStart(16, '0')
    .slice(-16)
}

function fallbackHashHex(text: string) {
  let h1 = 0x811c9dc5
  let h2 = 0x01000193
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i)
    h1 ^= code
    h1 = Math.imul(h1, 0x01000193)
    h2 ^= code + i
    h2 = Math.imul(h2, 0x811c9dc5)
  }
  const part = (n: number) => (n >>> 0).toString(16).padStart(8, '0')
  return `${part(h1)}${part(h2)}${part(h1 ^ h2)}${part(Math.imul(h1, h2))}`.padEnd(64, '0')
}
